import debounce from 'lodash/debounce'

import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, Outlet } from 'react-router-dom'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Artist } from 'types/artist'
import emptyArtists1x from 'assets/images/empty-artists.png'
import emptyArtists2x from 'assets/images/empty-artists@2x.png'
import emptySearch1x from 'assets/images/empty-search.png'
import emptySearch2x from 'assets/images/empty-search@2x.png'
import {
  loadArtists,
  loadNextArtists,
} from '@disco/state-management/actions/artist-actions'
import { getArtists } from '@disco/state-management/selectors/artists'
import { useComposerInsteadOfArtist } from '@disco/utils/composerInsteadOfArtist'
import ArtistGridItem from 'components/common/artist-grid-item'
import Row from 'components/common/row'
import DocumentHeadTags from 'components/document-head-tags'
import Button from 'components/ui/button'
import EmptyState from 'components/ui/empty-state'
import Grid from 'components/ui/grid'
import InfiniteScroll from 'components/ui/infinite-scroll'
import Input from 'components/ui/input'
import PageContainer from 'components/ui/page-container'
import PageHeader from 'components/ui/page-header'
import Tooltip from 'components/ui/tooltip'

const LibrariesArtists = (): React.ReactNode => {
  const location = useLocation()
  const dispatch = useDispatch()
  const artists = useSelector(getArtists)
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState('is_in_client_libraries')
  const [isLoadingNext, setIsLoadingNext] = useState(false)
  const [isSearching, setIsSearching] = useState(false)
  const typeData = useComposerInsteadOfArtist()
  useEffect(() => {
    dispatch(loadArtists(query, filter))
  }, [])
  useEffect(() => {
    if (artists?.invalidate) {
      dispatch(loadArtists(query, filter))
    }
  }, [artists?.invalidate])
  const debouncedQuery = useRef(
    debounce((query, filter) => {
      setIsSearching(false)
      dispatch(loadArtists(query, filter))
    }, 300),
  ).current

  const updateSearchQuery = (event: React.ChangeEvent<HTMLInputElement>) => {
    setIsLoadingNext(false)
    setIsSearching(true)
    setQuery(event.target.value)
    debouncedQuery(event.target.value, filter)
  }

  const loadNext = (next: string) => {
    setIsLoadingNext(true)
    dispatch(loadNextArtists(next))
  }

  const renderArtists = (artists: {
    loading: boolean
    loaded: boolean
    nextPage: string | null
    list: Artist[]
  }) =>
    artists.loaded && artists.list.length === 0 ? (
      renderEmptyState()
    ) : (
      <InfiniteScroll next={artists.nextPage} loadNext={loadNext}>
        <Grid size="small">
          {artists.list.map((artist: Artist) => (
            <ArtistGridItem
              key={artist['id']}
              image={artist['image_url']}
              title={artist['name']}
              overlayActions={
                <Tooltip content={`Edit ${typeData.value}`}>
                  <Button
                    appearance="icon"
                    compact
                    icon="edit"
                    link={`/catalogs/artists/${artist['id']}`}
                  />
                </Tooltip>
              }
              imageParams={{
                crop: 'faces',
              }}
            />
          ))}
        </Grid>
      </InfiniteScroll>
    )

  const renderEmptyState = () =>
    !isSearching && query === '' && !filter ? (
      <EmptyState>
        <img
          srcSet={`${emptyArtists1x}, ${emptyArtists2x} 2x`}
          width="194"
          height="185"
        />
        <p>No artists here yet.</p>
      </EmptyState>
    ) : (
      <EmptyState>
        <img
          srcSet={`${emptySearch1x}, ${emptySearch2x} 2x`}
          width="177"
          height="160"
        />
        <p>Sorry! We couldn’t find any artists for that search.</p>
      </EmptyState>
    )

  const FILTER_OPTIONS = useMemo(
    () => [
      {
        label: 'All',
        selected: filter === 'is_in_client_libraries',
        value: 'is_in_client_libraries',
      },
      {
        disabled: true,
        label: 'Artists with profiles',
        value: 'Artists with profiles',
      },
      {
        disabled: true,
        label: 'Artists without profiles',
        value: 'Artists without profiles',
      },
    ],
    [filter],
  )
  return location.state?.modal ? (
    <Outlet />
  ) : (
    <div className="Page">
      <DocumentHeadTags title={`All ${typeData.title}s | DISCO`} />
      <div className="Page_Content">
        <PageHeader
          breadcrumbs={{
            content: 'Catalogs',
            link: '/catalogs',
          }}
          title={`All ${typeData.title}s`} // actions={
          //   <EllipsisMenu
          //     caption="Filter by"
          //     options={FILTER_OPTIONS}
          //     onOptionClick={filter => {
          //       setFilter(filter)
          //       setIsLoadingNext(false)
          //       setIsSearching(true)
          //       debouncedQuery(query, filter)
          //     }}
          //   />
          // }
        />
        <PageContainer>
          <Row columnsSize={[3]}>
            <div>
              <Input
                icon="search"
                placeholder="Search"
                value={query}
                onChange={updateSearchQuery}
              />
            </div>
          </Row>
          {!isLoadingNext && artists.loading ? (
            <span>Loading...</span>
          ) : (
            renderArtists(artists)
          )}
        </PageContainer>
      </div>
      <Outlet />
    </div>
  )
}

export default LibrariesArtists
